import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mockSales } from '@/mocks/tr/sales';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    'Tamamlandı': 'bg-green-50 text-green-600 border-green-200',
    'Kısmi Ödendi': 'bg-blue-50 text-blue-600 border-blue-200',
    'Bekliyor': 'bg-gold-50 text-gold-600 border-gold-200',
    'Veresiye': 'bg-rose-50 text-rose-600 border-rose-200',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === 'Tamamlandı' ? 'bg-green-500' : status === 'Kısmi Ödendi' ? 'bg-blue-500' : status === 'Gecikti' ? 'bg-red-500' : 'bg-gold-500'}`} />
      {status}
    </span>
  );
}

export default function Sales() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return mockSales.filter((s) => {
      const matchSearch =
        s.customer_name.toLowerCase().includes(search.toLowerCase()) ||
        s.product_description.toLowerCase().includes(search.toLowerCase()) ||
        s.phone.includes(search);
      const matchStatus = !filterStatus || s.status === filterStatus;
      return matchSearch && matchStatus;
    });
  }, [search, filterStatus]);

  const handlePayment = (saleId: string) => {
    setSelectedSale(saleId);
    setShowPaymentModal(true);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-boutique-black">{t('sales.title')}</h1>
          <p className="text-sm text-boutique-warm mt-1">Satışları görüntüleyin, düzenleyin ve yeni satış ekleyin</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-boutique-black text-white rounded-lg text-sm font-medium hover:bg-boutique-charcoal transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" />
          {t('sales.newSale')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 bg-white p-4 rounded-xl border border-cream-200">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-boutique-warm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search')}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none transition-all text-sm"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none transition-all text-sm bg-white sm:w-48"
        >
          <option value="">{t('common.all')}</option>
          <option value="Tamamlandı">{t('common.completed')}</option>
          <option value="Kısmi Ödendi">{t('common.partial')}</option>
          <option value="Veresiye">{t('common.credit')}</option>
          <option value="Bekliyor">{t('common.pending')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-cream-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream-50 border-b border-cream-200">
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Tarih</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Müşteri</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Telefon</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Ürün</th>
                <th className="text-right px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Toplam</th>
                <th className="text-right px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Ödenen</th>
                <th className="text-right px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Kalan</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Ödeme</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Durum</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-100">
              {filtered.map((sale) => (
                <tr key={sale.id} className="hover:bg-cream-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-boutique-black">{sale.sale_date}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-medium text-boutique-black">{sale.customer_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-boutique-warm">{sale.phone}</td>
                  <td className="px-4 py-3 text-boutique-warm max-w-xs truncate">{sale.product_description}</td>
                  <td className="px-4 py-3 text-right font-semibold text-boutique-black whitespace-nowrap">{formatCurrency(sale.total_amount)}</td>
                  <td className="px-4 py-3 text-right text-green-600 font-medium whitespace-nowrap">{formatCurrency(sale.paid_amount)}</td>
                  <td className="px-4 py-3 text-right text-red-600 font-medium whitespace-nowrap">{formatCurrency(sale.remaining_amount)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-boutique-warm">{sale.payment_type}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusBadge status={sale.status} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {sale.remaining_amount > 0 && (
                        <button
                          onClick={() => handlePayment(sale.id)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-green-600 hover:bg-green-50 transition-colors"
                          title={t('common.payment')}
                        >
                          <i className="ri-money-cny-circle-line text-base" />
                        </button>
                      )}
                      <button className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:bg-cream-100 transition-colors" title={t('common.detail')}>
                        <i className="ri-eye-line text-base" />
                      </button>
                      <button className="w-8 h-8 rounded-lg flex items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors" title={t('common.edit')}>
                        <i className="ri-edit-line text-base" />
                      </button>
                      <button className="w-8 h-8 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors" title={t('common.delete')}>
                        <i className="ri-delete-bin-line text-base" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-shopping-bag-line text-4xl text-cream-300 mb-3" />
            <p className="text-sm text-boutique-warm">{t('common.noData')}</p>
          </div>
        )}
      </div>

      {/* New Sale Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-cream-200 flex items-center justify-between">
              <h2 className="font-semibold text-boutique-black text-lg">{t('sales.newSale')}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:bg-cream-100 transition-colors">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-boutique-black mb-2">Müşteri Adı</label>
                  <input className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="Müşteri adı" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-boutique-black mb-2">Telefon</label>
                  <input className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="05xx xxx xx xx" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Satış Tarihi</label>
                <input type="date" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Ürün Açıklaması</label>
                <textarea className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm resize-none h-20" placeholder="Satılan ürünlerin açıklaması" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-boutique-black mb-2">Toplam Tutar</label>
                  <input type="number" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-boutique-black mb-2">Ödenen Tutar</label>
                  <input type="number" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="0.00" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Ödeme Tipi</label>
                <select className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm bg-white">
                  <option>Nakit</option>
                  <option>Kart</option>
                  <option>Havale</option>
                  <option>Parçalı</option>
                  <option>Veresiye</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Notlar</label>
                <textarea className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm resize-none h-16" placeholder="Ek notlar..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 px-4 border border-cream-200 text-boutique-black rounded-lg text-sm font-medium hover:bg-cream-50 transition-colors">
                  {t('common.cancel')}
                </button>
                <button className="flex-1 py-2.5 px-4 bg-boutique-black text-white rounded-lg text-sm font-medium hover:bg-boutique-charcoal transition-colors">
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedSale && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-cream-200 flex items-center justify-between">
              <h2 className="font-semibold text-boutique-black text-lg">{t('common.payment')}</h2>
              <button onClick={() => setShowPaymentModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:bg-cream-100 transition-colors">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-cream-50 rounded-lg p-4">
                <p className="text-sm text-boutique-warm">Müşteri</p>
                <p className="font-medium text-boutique-black">{mockSales.find((s) => s.id === selectedSale)?.customer_name}</p>
                <p className="text-sm text-red-600 font-medium mt-1">
                  Kalan: {formatCurrency(mockSales.find((s) => s.id === selectedSale)?.remaining_amount || 0)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Ödeme Tutarı</label>
                <input type="number" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Ödeme Tarihi</label>
                <input type="date" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Ödeme Tipi</label>
                <select className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm bg-white">
                  <option>Nakit</option>
                  <option>Kart</option>
                  <option>Havale</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowPaymentModal(false)} className="flex-1 py-2.5 px-4 border border-cream-200 text-boutique-black rounded-lg text-sm font-medium hover:bg-cream-50 transition-colors">
                  {t('common.cancel')}
                </button>
                <button className="flex-1 py-2.5 px-4 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}